import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Skeleton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { commonContainerWrapper } from "../../Common/styles/flex";
import {
  RandomizationErrorLike,
  randomizationErrorMessage,
} from "./serverErrors";

/**
 * The four states every randomization list and panel must implement:
 * loading skeleton, empty message, error with retry, content. Spec 21.8.
 *
 * They live in one place because the requirement is easy to satisfy by
 * accident and hard to satisfy consistently: three of the four are the same in
 * every screen, and a screen that renders `data.items.map(...)` against an
 * undefined `data` during loading crashes rather than showing a skeleton.
 *
 * THE ERROR STATE ALWAYS OFFERS RETRY, and never silently swallows the
 * message. The server returns an i18n key from spec section 23 whose message
 * says what to do next — "no randomization slots remain for this stratum,
 * extend the list" is actionable, and replacing it with "Something went wrong"
 * throws that away. `errorMessage` is looked up in the `randomization`
 * namespace and falls back to the raw text when the key is unknown, so an
 * unexpected server error still shows something rather than a blank box.
 *
 * @category Randomization
 */

/** Rows of skeletons, sized to the table they replace so the layout does not
 *  jump when the data lands. */
export const TableLoadingState = ({ rows = 5 }: { rows?: number }) => (
  <Box sx={{ py: 1 }}>
    {Array.from({ length: rows }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rectangular"
        height={44}
        sx={{ mb: 1, borderRadius: "8px" }}
      />
    ))}
  </Box>
);

export const PanelLoadingState = () => (
  <Box sx={{ ...commonContainerWrapper }}>
    <Skeleton variant="text" width="40%" height={28} />
    <Skeleton variant="rectangular" height={90} sx={{ mt: 1, borderRadius: "8px" }} />
  </Box>
);

export const EmptyState = ({ message }: { message?: string }) => {
  const { t } = useTranslation("randomization");
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="body1" color="text.secondary">
        {message ?? t("ui.empty")}
      </Typography>
    </Box>
  );
};

/**
 * @param error the RTK Query error. Its `message` is the server's i18n key.
 * @param onRetry re-runs the query. Always offered.
 */
export const ErrorState = ({
  error,
  onRetry,
}: {
  error?: RandomizationErrorLike | unknown;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation("randomization");
  // One place decides how a server key becomes a sentence: see
  // `serverErrors.ts`. Duplicating the lookup here is how the toast and the
  // inline alert end up disagreeing about the same failure.
  const message = randomizationErrorMessage(error, t as never);

  return (
    <Alert
      severity="error"
      sx={{ my: 2 }}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t("ui.retry")}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{t("ui.errorTitle")}</AlertTitle>
      {message}
    </Alert>
  );
};

/** The feature-disabled state of spec 21.1: explanatory text and nothing else,
 *  shown even to a user holding every randomization permission. */
export const FeatureDisabledState = () => {
  const { t } = useTranslation("randomization");
  return (
    <Box sx={{ ...commonContainerWrapper }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("ui.disabled.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t("ui.disabled.body")}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t("ui.disabled.note")}
      </Typography>
    </Box>
  );
};

/**
 * No randomization permission on this study — which is NOT the same as the
 * feature being off, and used to be reported as if it were.
 *
 * `RandomizationTab` rendered `FeatureDisabledState` both when the feature was
 * disabled and when the user held none of the sub-tab permissions. So a study
 * with randomization plainly ticked on the features screen displayed
 * "Randomization is not enabled", and the administrator went looking for a
 * feature toggle that was already on. The two causes need different actions:
 * one is a feature toggle, the other is a role grant.
 */
export const NoRandomizationPermissionState = () => {
  const { t } = useTranslation("randomization");
  return (
    <Box sx={{ ...commonContainerWrapper }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("ui.noPermission.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t("ui.noPermission.body")}
      </Typography>
    </Box>
  );
};

// Re-exported so callers can keep importing the error shape from the module
// they already depend on.
export type { RandomizationErrorLike };
